import { useEffect, useState } from 'react';
import { bindActionCreators, Dispatch } from 'redux';
import { connect, RootStateOrAny } from 'react-redux';

import { Select, Input, Form, Button, Modal, Table } from 'antd';

import { ErrorResponse } from 'utils/types';
import { showGenericError, getDisplayCompanyId } from 'utils/helpers';
import { ORGANIZATION_NAME } from 'utils/objects';

import { getEzcapIpasDesc } from 'store/app/ipa/actions';
import { getEzcapClaimStatus } from 'store/claim/actions'

import FloatLabel from 'components/Common/FloatLabel';

const { Option } = Select;

type PublicClaimStatusModalProps = {
  getEzcapIpasDesc(): any,
  getEzcapClaimStatus(companyId: string, claimNo: string): any,
  isFetching: boolean,
  isModalVisible: boolean,
  isModalVisibleHandler(): any,
  setVisible(visible: boolean): void,
};

const PublicClaimStatusModal = (props: PublicClaimStatusModalProps) => {

  const [form] = Form.useForm();
  const [companyIdOptions, setCompanyIdOptions] = useState<any[]>([]);
  const [companyId, setCompanyId] = useState('');
  const [claimNo, setClaimNo] = useState('');
  const [isClaimStatusTableVisible, setIsClaimStatusTableVisible] = useState(false); 
  const [claimStatusTableDataSource, setClaimStatusTableDataSource] = useState<Object[]>([]); 
  const [claimStatusError, setClaimStatusError] = useState('');
  
  useEffect(() => {
    if (props.isModalVisible) {
      getEzcapIpasList();
    }
  }, [props.isModalVisible]);

  const claimStatusTableColumn = [
    {
      title: 'IPA',
      key: 'ipa',
      dataIndex: 'ipa'
    },
    {
      title: 'Claim No.',
      key: 'claimNo',
      dataIndex: 'claimNo'
    },
    {
      title: 'Status',
      key: 'status',
      dataIndex: 'status'
    },
  ];
  
  const getEzcapIpasList = () => {
    props.getEzcapIpasDesc()
      .then((response: any[]) => {
        setCompanyIdOptions(response);
      })
      .catch((err: ErrorResponse) => {
        showGenericError(err);
      });
  };  

  const handleSelectChange = (value: string) => {
    setCompanyId(value);
  }; 

  const getEzcapClaimStatus = (companyId: string, claimNo: string) => {    
    props.getEzcapClaimStatus(companyId, claimNo)
      .then((response: string) => {
        let claimStatus = "";
        switch(response) {
          case 'INVALID_STATUS':
            claimStatus = "Contact Customer Service";
            setClaimStatusError('');
            break;
          case 'INVALID_CLAIM':
            setClaimStatusError('There were no results for this search. Please check the fields and try again.');
            setIsClaimStatusTableVisible(false);
            return;
          default:
            claimStatus = response;
            setClaimStatusError('');
        }

        setClaimStatusTableDataSource([{
          ipa: companyId,
          claimNo:claimNo,
          status: claimStatus,
        }]);
        setIsClaimStatusTableVisible(true);
      })
      .catch((err: ErrorResponse) => {
        setIsClaimStatusTableVisible(false);
        showGenericError(err);
      });
  }

  const onSearch = () => {    
    form.validateFields().then(value => {
      getEzcapClaimStatus(value.companyId, value.claimNo);
    });
  }

  const onReset = () => {
    setIsClaimStatusTableVisible(false);
    setClaimStatusTableDataSource([]);
    setClaimStatusError('');
    form.resetFields();
  }

  const closeModal = () => {
    setClaimStatusTableDataSource([]);
    setIsClaimStatusTableVisible(false);
    setClaimStatusError('');
    form.resetFields();
    props.isModalVisibleHandler();
  }

  return (
    <>
      <Modal
        className="ezcap-claim-status-modal"
        visible={props.isModalVisible}
        title="Claim Status"
        width={1000}
        onCancel={closeModal}
        footer={[
          <>
            <div className="text-center mt-2 mb-2">
              <a href="https://www.networkmedicalmanagement.com/contact" target="_blank">Contact Customer Service</a>
            </div>
            <div className="text-center mt-2 mb-2">
              &copy; {ORGANIZATION_NAME}, Inc., All Rights Reserved
            </div>
          </>
        ]}
      >
        <Form form={form} name={'claimStatusForm'} onFinish={onSearch}>
        <div className="row">
          <div className="col-lg-6 col-md-4">
          <FloatLabel
            label="Select an IPA:"
            name="companyId"
            form={form}
            value={companyId}
            required
          >
              <Select
                onSelect={handleSelectChange}
                showSearch
              >
                {companyIdOptions.map((option, index) => {
                  return <Option key={index} value={option.companyId}>{getDisplayCompanyId(option.companyId)}</Option>
                })}
              </Select>
            </FloatLabel>
          </div>
          <div className="col-lg-6 col-md-4">
            <FloatLabel
              label="Claim No"
              name={"claimNo"}
              form={form}
              value={claimNo}
              required
            >
              <Input />
            </FloatLabel>
          </div>
          <div className="col-lg-6 col-md-6 mt-2 mb-4 btn-wrapper">
            <Button type="primary" htmlType={'submit'} loading={props.isFetching} style={{ width: '50%' }}>SEARCH</Button>
            <Button type="default" onClick={onReset} style={{ width: '50%' }}>RESET</Button>  
          </div>

          <hr className="claim-status-form-modal-divider" />

          <div className="col-lg-24 mb-4">
            {isClaimStatusTableVisible  && claimStatusError === ''? 
              <Table
                className="claim-status-table"
                columns={claimStatusTableColumn}
                dataSource={claimStatusTableDataSource}              
                loading={props.isFetching} 
                pagination={false}
              />
              : null
            }
            {!isClaimStatusTableVisible && claimStatusError !== '' ? 
              <div>
                {claimStatusError}
              </div> 
              : null
            }
          </div>
        </div>
        </Form>
      </Modal>
    </>
  )};

  const mapStateToProps = (store: RootStateOrAny) => ({
    isFetching: store.rootReducer.claim.isFetchingEzClaimStatus,
  });

  const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators(
    {
      getEzcapIpasDesc,
      getEzcapClaimStatus,
    },
      dispatch,
  );

  export default connect(mapStateToProps, mapDispatchToProps)(PublicClaimStatusModal);